import React, { useState } from "react";

const QuickHistoryModal = ({ closeQuickHistoryModal, isQuickHistoryModalOpen, allDagNodes }) => {
  const [selectedNode, setSelectedNode] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isQuickHistoryModalOpen) return null;

  const handleNodeSelection = async (nodeSlug) => {
    if (!nodeSlug) {
      setHistoryData([]);
      setSelectedNode('');
      return;
    }

    setSelectedNode(nodeSlug);
    setLoading(true);
    setError('');

    try {
      const baseUrl = `${window.location.origin}${window.location.pathname}`;
      const response = await fetch(baseUrl + `/node_history?dag_node_name=${nodeSlug}`);

      if (!response.ok) {
        throw new Error('Failed to fetch node history');
      }

      const data = await response.json();
      
      if (response.ok) {
        setHistoryData(data.history || []);
      } else {
        throw new Error(data.error || 'Failed to fetch node history');
      }
    } catch (err) {
      console.error('Error fetching node history:', err);
      setError(err.message || 'Failed to load node history. Please try again.');
      setHistoryData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Normalize the date string for better cross-browser compatibility
      let normalizedDate = dateString;
      
      // Handle ISO date strings without timezone info by adding UTC
      if (typeof dateString === 'string' && !dateString.includes('T') && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        normalizedDate = dateString + 'T00:00:00Z';
      }
      
      // Handle dates that might be in format "YYYY-MM-DD HH:MM:SS" (without timezone)
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        normalizedDate = dateString.replace(' ', 'T') + 'Z';
      }
      
      const date = new Date(normalizedDate);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return dateString; // Return original string if parsing fails
      }
      
      // Use consistent formatting options for cross-platform compatibility
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.warn('Date formatting error:', error, 'for date:', dateString);
      return dateString;
    }
  };

  const formatBoolean = (value) => {
    return value ? 'Yes' : 'No';
  };

  return (
    <div className="modal fixed inset-0 flex items-center justify-center z-50">
      <div style={{ backgroundColor: '#f1f0f5', height: '90%', width: '80%' }} className="bg-white rounded-lg shadow-lg p-6 flex flex-col">
        <div className="flex justify-between items-center mb-6">
          <h1 className="modal-title text-2xl font-semibold text-gray-800">Quick Node History</h1>
          <i className="bi bi-x-lg cursor-pointer text-xl hover:text-gray-600" onClick={closeQuickHistoryModal}></i>
        </div>

        {/* Node Selection */}
        <div className="mb-6">
          <label htmlFor="quickHistoryNodeSelector" className="block text-sm font-semibold text-gray-700 mb-2">
            Select Node:
          </label>
          <select 
            id="quickHistoryNodeSelector" 
            className="border border-gray-300 p-3 rounded-md w-full max-w-md"
            value={selectedNode}
            onChange={(e) => handleNodeSelection(e.target.value)}
            disabled={loading}
          >
            <option value="">-- Select a node --</option>
            {allDagNodes?.map((node, index) => (
              <option key={index} value={node.name}>{node.name}</option>
            ))}
          </select>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="text-lg text-gray-600">Loading node history...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* History Data Table */}
        {!loading && !error && historyData.length > 0 && (
          <div className="flex-1 overflow-hidden flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              History for: {allDagNodes?.find(node => node.name === selectedNode)?.name}
            </h3>
            <div className="flex-1 overflow-auto border border-gray-300 rounded-lg">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Version</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Completed At</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Notes</th>
                    {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Optional</th> */}
                    {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Inactive</th> */}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Updated At</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Updated By</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {historyData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.version}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{row.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={row.description}>
                        {row.description || ''}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {row.due_date ? formatDate(row.due_date) : ''}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {row.completed_at ? formatDate(row.completed_at) : ''}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={row.notes}>
                        {row.notes || ''}
                      </td>
                      {/* <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatBoolean(row.optional)}
                      </td> */}
                      {/* <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatBoolean(row.inactive)}
                      </td> */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(row.updated_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {row.user_last_updated || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && selectedNode && historyData.length === 0 && (
          <div className="flex justify-center items-center py-8 text-gray-500">
            No history found for the selected node.
          </div>
        )}

        {/* Initial State */}
        {!loading && !error && !selectedNode && (
          <div className="flex justify-center items-center py-8 text-gray-500">
            Please select a node to view its history.
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickHistoryModal;
